import { Accordion } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import {
  QuestionCircleFill,
  PlayCircleFill,
  PersonBadgeFill,
  Controller,
} from 'react-bootstrap-icons';

const FAQ_GROUPS = [
  {
    label: 'Getting Started',
    icon: <PlayCircleFill />,
    items: [
      {
        question: 'How do I download the game?',
        answer: (
          <>
            <p>
              Download the installer from our{' '}
              <LinkContainer to="/download">
                <span className="faq-link">Downloads</span>
              </LinkContainer>{' '}
              page and run it to install the game.
            </p>
            <p>
              Conquer Online files are not antivirus-friendly, so you may encounter issues after
              installing. We recommend turning your antivirus off once the download is complete,
              then adding an exclusion for the game folder before turning it back on.
            </p>
          </>
        ),
      },
      {
        question: 'How do I find out when events are held?',
        answer: (
          <p>
            The{' '}
            <LinkContainer to="/eventschedule">
              <span className="faq-link">Event Schedule</span>
            </LinkContainer>{' '}
            page lists all server-held events and their times.
          </p>
        ),
      },
    ],
  },
  {
    label: 'Account & Security',
    icon: <PersonBadgeFill />,
    items: [
      {
        question: 'Is Account Sharing allowed?',
        answer: (
          <>
            <p>Account sharing is not supported by Paragon Conquer and is entirely at your own risk.</p>
            <p>
              We take no responsibility for actions taken by someone you have allowed access to your
              account. If your account is stolen or damaged as a result of sharing, we are unable to
              assist with recovering anything lost.
            </p>
            <p><strong>Never share your login credentials with anyone who asks for them.</strong></p>
          </>
        ),
      },
      {
        question: 'How do I change my account information?',
        answer: (
          <p>
            Log in to your account and use the <strong>Account Dropdown Menu</strong> in the
            top-right corner of the page. From there you can update your email address and password.
          </p>
        ),
      },
      {
        question: 'I forgot my username!',
        answer: (
          <>
            <p>
              To retrieve your username you must know the email address associated with your account.
              You can request a list of all usernames registered to that email to be sent to your
              inbox.
            </p>
            <p>If you do not know the email on the account, it cannot be recovered.</p>
          </>
        ),
      },
      {
        question: 'I forgot my password!',
        answer: (
          <>
            <p>You can reset your password by following these steps:</p>
            <ol>
              <li>Click <strong>Forgot Password</strong> on the login screen.</li>
              <li>Enter your username.</li>
              <li>Check your inbox for a <strong>Password Reset</strong> email from <strong>no-reply@paragonconquer.com</strong>.</li>
              <li>Click the reset link — it expires after <strong>30 minutes</strong>.</li>
              <li>Choose a new password.</li>
            </ol>
          </>
        ),
      },
    ],
  },
  {
    label: 'In-Game Help',
    icon: <Controller />,
    items: [
      {
        question: 'How do I report a player?',
        answer: (
          <>
            <p>You can report a player through any of the following methods:</p>
            <ul>
              <li>
                <strong>Submit a Ticket</strong> — log in, go to the Ticket Tracker, and submit a
                new ticket with any evidence or information about the rule violation.
              </li>
              <li><strong>Contact a GM</strong> — reach out to a GM in-game or through Discord.</li>
              <li><strong>Discord</strong> — post in the <strong>#support</strong> channel.</li>
            </ul>
          </>
        ),
      },
      {
        question: "I'm stuck on a quest — what should I do?",
        answer: (
          <>
            <p>
              The{' '}
              <LinkContainer to="/guide">
                <span className="faq-link">Guide</span>
              </LinkContainer>{' '}
              has comprehensive image and video walkthroughs for all quests.
            </p>
            <p>If you are still stuck after checking the Guide, visit our Discord to request further assistance.</p>
          </>
        ),
      },
    ],
  },
];

export function FrequentlyAskedQuestions() {
  return (
    <div className="faq-page">
      <div className="faq-hero">
        <div className="faq-hero-icon"><QuestionCircleFill /></div>
        <h2>Frequently Asked Questions</h2>
        <p>Can't find an answer? Reach out to us on Discord.</p>
      </div>

      {FAQ_GROUPS.map((group, gi) => (
        <div key={gi} className="faq-group">
          <div className="faq-group-label">
            <span className="faq-group-icon">{group.icon}</span>
            {group.label}
          </div>
          <Accordion className="faq-accordion">
            {group.items.map((item, ii) => (
              <Accordion.Item key={ii} eventKey={String(ii)}>
                <Accordion.Header>{item.question}</Accordion.Header>
                <Accordion.Body>{item.answer}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>
      ))}

      <div className="faq-note">
        <p>Still have questions? Contact us through the website or join us on Discord.</p>
      </div>
    </div>
  );
}
